import React, { useEffect, MouseEvent, useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

/* -------------------------------------------------------------------------
 * ShareOptionsPopup
 * -------------------------------------------------------------------------
 * A modal that allows users to copy a share link or share directly via
 * common social platforms.
 * -------------------------------------------------------------------------
 * Props
 * -----
 * isOpen     : Whether the modal is visible.
 * chartName  : The name of the chart being shared (used in labels).
 * shareLink  : The URL users can copy/share.
 * onClose    : Callback for closing the modal.
 * ------------------------------------------------------------------------- */

export interface ShareOptionsPopupProps {
  isOpen: boolean;
  chartName: string;
  shareLink: string;
  onClose: () => void;
}

export const ShareOptionsPopup: React.FC<ShareOptionsPopupProps> = ({
  isOpen,
  chartName,
  shareLink,
  onClose,
}) => {
  /* ---------------------------------------------------------------------
   * Side-effects
   * ------------------------------------------------------------------- */
  // Lock background scrolling while modal is open, unlock when closed.
  useEffect(() => {
    if (!isOpen) return;

    const { overflow: originalOverflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  /* ---------------------------------------------------------------------
   * Local state
   * ------------------------------------------------------------------- */
  type CopyStage = 'idle' | 'bouncing' | 'static';
  const [copyStage, setCopyStage] = useState<CopyStage>('idle');

  /* ---------------------------------------------------------------------
   * Refs for focus and keyboard handling
   * ------------------------------------------------------------------- */
  const containerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Autofocus close button when popup opens
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  // Close on ESC when focus is inside popup
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (containerRef.current?.contains(document.activeElement)) {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  /* ---------------------------------------------------------------------
   * Toast
   * ------------------------------------------------------------------- */
  const { toast } = useToast();

  /* ---------------------------------------------------------------------
   * Handlers
   * ------------------------------------------------------------------- */
  const handleBackdropClick = () => onClose();

  const handleContainerClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent backdrop click when interacting inside modal.
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);

      // Start bounce animation
      setCopyStage('bouncing');

      toast({
        title: "Link Copied",
        description: "The share link has been copied to your clipboard.",
      });

      // After bounce cycle (~800ms), switch to static checkmark
      setTimeout(() => setCopyStage('static'), 1200);

      // After an additional 1 second, revert back to idle state
      setTimeout(() => setCopyStage('idle'), 2600);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy the link. Please try again.",
        variant: "destructive",
      });
    }
  };

  /* ---------------------------------------------------------------------
   * JSX
   * ------------------------------------------------------------------- */
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 backdrop-filter backdrop-grayscale"
      onClick={handleBackdropClick}
    >
      <div
        ref={containerRef}
        className="relative w-full max-w-[600px] rounded-lg bg-white p-10 shadow-[0_4px_24px_rgba(0,0,0,0.15)] pt-8"
        role="dialog"
        aria-modal="true"
        onClick={handleContainerClick}
      >
        {/* Top-right close button */}
        <div className="absolute top-6 right-8 flex items-center gap-4">
          <button
            ref={closeButtonRef}
            aria-label="Close"
            onClick={onClose}
            className="flex h-12 w-12 items-center justify-center text-[#212121] transition-colors hover:text-gray-900 focus:outline-none"
          >
            <span className="material-symbols-outlined pointer-events-none text-[31px] font-light leading-none">
              close
            </span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <span className="material-symbols-outlined flex h-8 w-8 items-center justify-center text-[30px] text-[#212121] font-light leading-none">
            share
          </span>
          <h2
            className="break-words font-geist text-[24px] font-bold text-[#212121]"
            style={{ lineHeight: '1.3', fontSize: '24px', fontWeight: '550'}}
          >
            Share {chartName}
          </h2>
        </div>

        {/* Description */}
        <p
          className="mt-6 mb-6 font-inter text-[16px] font-normal leading-[1.3] text-[#333333] font-weight-400"
        >
          Share your benchmarking results with others by copying the link below.
        </p>

        {/* Share link */}
        <label className="mb-2 block text-[18px] text-[#3A3A3A] font-weight-500">
          Share Link
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            readOnly
            value={shareLink}
            className="flex-grow overflow-hidden text-ellipsis rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-sm text-[#212121]"
          />
          <button
            aria-label="Copy link"
            onClick={handleCopy}
            className="relative rounded-md bg-[#F3F4F6] w-10 h-10 hover:bg-[#E5E7EB] transition-transform"
          >
            <span
              className={
                `material-symbols-outlined w-10 h-8 text-[20px] leading-none relative top-[6px] ${
                  copyStage === 'idle' ? 'text-[#212121]' : 'text-green-600' 
                } ${copyStage === 'bouncing' ? 'animate-check-bounce' : ''}`
              }
            >
              {copyStage === 'idle' ? 'content_copy' : 'check'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

ShareOptionsPopup.displayName = "ShareOptionsPopup"; 